/**
 * EmailFilterPicker — right-docked side-panel for adding a new email binding.
 *
 * Pulls Gmail labels + Outlook folders from /email-list-filters. Each row gets
 * an "Add" button that creates a v9_email_filter_bindings row for the current
 * user. The user can also add a raw saved search (Gmail q= or Outlook $search).
 *
 * Side-panel (Dialog with right-anchored content), NEVER a modal with a blur
 * wall. Closes on ESC, on overlay click, or on success.
 */
import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Plus, Search, AlertCircle, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';
import {
  createBinding,
  listAvailableFilters,
  type EmailProvider,
  type EmailFilterKind,
} from '../../lib/email';

interface Props {
  tenantId: string;
  userId: string;
  onClose: () => void;
  onCreated: () => void;
}

interface FilterListResponse {
  gmail: { connected: boolean; labels: { id: string; name: string; type: string; messagesUnread?: number; messagesTotal?: number }[]; error?: string };
  outlook: { connected: boolean; folders: { id: string; displayName: string; unreadItemCount: number; totalItemCount: number }[]; error?: string };
}

export function EmailFilterPicker({ tenantId, userId, onClose, onCreated }: Props) {
  const [data, setData] = useState<FilterListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);
  const [tab, setTab] = useState<'gmail' | 'outlook' | 'search'>('gmail');
  const [searchProvider, setSearchProvider] = useState<EmailProvider>('gmail');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLabel, setSearchLabel] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const json = await listAvailableFilters();
        if (!cancelled) setData(json as FilterListResponse);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAdd = async (input: {
    provider: EmailProvider;
    filter_kind: EmailFilterKind;
    filter_value: string;
    display_label: string;
  }) => {
    const key = `${input.provider}:${input.filter_kind}:${input.filter_value}`;
    setAdding(key);
    setError(null);
    try {
      await createBinding({ tenantId, userId, ...input });
      onCreated();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setAdding(null);
    }
  };

  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-neutral-950/40" />
        <Dialog.Content
          className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-white/10 bg-neutral-950 shadow-2xl"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <header className="flex shrink-0 items-center justify-between border-b border-white/10 px-4 py-3">
            <div>
              <Dialog.Title className="text-sm font-semibold">Add email filter</Dialog.Title>
              <Dialog.Description className="mt-0.5 text-[11px] text-neutral-500">
                Pick a Gmail label or Outlook folder — or save a raw search.
              </Dialog.Description>
            </div>
            <Dialog.Close
              className="rounded-full p-1 text-neutral-400 hover:bg-white/5 hover:text-neutral-100"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Dialog.Close>
          </header>

          <nav className="flex shrink-0 gap-1 border-b border-white/5 px-2 py-1.5 text-xs">
            {(['gmail', 'outlook', 'search'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={clsx(
                  'rounded-md px-3 py-1.5 capitalize transition',
                  t === tab ? 'bg-brand-600/20 text-brand-100' : 'text-neutral-400 hover:text-neutral-100',
                )}
              >
                {t}
              </button>
            ))}
          </nav>

          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {loading && (
              <div className="flex items-center gap-2 px-2 py-4 text-xs text-neutral-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
              </div>
            )}
            {error && (
              <div className="mb-3 flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/[0.04] p-2.5 text-xs text-red-200">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {tab === 'gmail' && data && (
              <div className="space-y-1">
                {!data.gmail.connected && (
                  <ProviderNotConnected provider="Google" />
                )}
                {data.gmail.connected && data.gmail.labels.length === 0 && (
                  <div className="px-2 py-4 text-xs text-neutral-500">No labels found.</div>
                )}
                {data.gmail.labels.map((l) => {
                  const key = `gmail:label:${l.id}`;
                  return (
                    <FilterRow
                      key={l.id}
                      label={l.name}
                      sub={l.type === 'system' ? 'System label' : 'User label'}
                      badge={l.messagesUnread ? `${l.messagesUnread} unread` : undefined}
                      busy={adding === key}
                      onAdd={() =>
                        handleAdd({
                          provider: 'gmail',
                          filter_kind: 'label',
                          filter_value: l.id,
                          display_label: l.name,
                        })
                      }
                    />
                  );
                })}
              </div>
            )}

            {tab === 'outlook' && data && (
              <div className="space-y-1">
                {!data.outlook.connected && (
                  <ProviderNotConnected provider="Microsoft 365" />
                )}
                {data.outlook.connected && data.outlook.folders.length === 0 && (
                  <div className="px-2 py-4 text-xs text-neutral-500">No folders found.</div>
                )}
                {data.outlook.folders.map((f) => {
                  const key = `outlook:folder:${f.id}`;
                  return (
                    <FilterRow
                      key={f.id}
                      label={f.displayName}
                      sub={`${f.totalItemCount} total`}
                      badge={f.unreadItemCount ? `${f.unreadItemCount} unread` : undefined}
                      busy={adding === key}
                      onAdd={() =>
                        handleAdd({
                          provider: 'outlook',
                          filter_kind: 'folder',
                          filter_value: f.id,
                          display_label: f.displayName,
                        })
                      }
                    />
                  );
                })}
              </div>
            )}

            {tab === 'search' && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!searchQuery || !searchLabel) return;
                  void handleAdd({
                    provider: searchProvider,
                    filter_kind: 'search',
                    filter_value: searchQuery,
                    display_label: searchLabel,
                  });
                }}
                className="space-y-3 p-1"
              >
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-neutral-500">Provider</label>
                  <div className="mt-1 flex gap-1 rounded-lg border border-white/10 bg-white/[0.03] p-0.5 text-xs">
                    {(['gmail', 'outlook'] as const).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setSearchProvider(p)}
                        className={clsx(
                          'flex-1 rounded-md px-2 py-1 capitalize',
                          p === searchProvider ? 'bg-brand-600/20 text-brand-100' : 'text-neutral-400 hover:text-neutral-100',
                        )}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-neutral-500">Display name</label>
                  <input
                    type="text"
                    className="input-control mt-1"
                    placeholder="Customer responses"
                    value={searchLabel}
                    onChange={(e) => setSearchLabel(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-neutral-500">
                    Search query{' '}
                    <span className="ml-1 text-neutral-600">
                      ({searchProvider === 'gmail' ? 'Gmail q=' : 'Outlook $search'} syntax)
                    </span>
                  </label>
                  <div className="relative mt-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-500" />
                    <input
                      type="text"
                      className="input-control pl-9"
                      placeholder={searchProvider === 'gmail' ? 'from:leads@ -label:archived' : 'from:leads@example.com'}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={!searchQuery || !searchLabel || adding === `${searchProvider}:search:${searchQuery}`}
                >
                  Add filter
                </button>
              </form>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function FilterRow({
  label,
  sub,
  badge,
  busy,
  onAdd,
}: {
  label: string;
  sub: string;
  badge?: string;
  busy: boolean;
  onAdd: () => void;
}) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-white/5 bg-white/[0.02] p-2 transition hover:bg-white/[0.04]">
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium text-neutral-100">{label}</div>
        <div className="text-[10px] text-neutral-500">{sub}</div>
      </div>
      {badge && (
        <span className="shrink-0 rounded-full bg-brand-500/10 px-1.5 py-0.5 text-[9px] font-medium text-brand-200">
          {badge}
        </span>
      )}
      <button
        type="button"
        onClick={onAdd}
        disabled={busy}
        className="rounded-md p-1.5 text-neutral-400 transition hover:bg-white/5 hover:text-neutral-100 disabled:opacity-50"
        aria-label="Add this filter"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

function ProviderNotConnected({ provider }: { provider: string }) {
  return (
    <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.04] p-3 text-xs text-amber-200">
      {provider} isn't connected yet. Head to Settings → Calendar to connect.
    </div>
  );
}
